import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import ContactModal from '../ContactModal/ContactModal';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import './Header.css';

const Header = () => {
  const headerRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { t, phone } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        '--header-height',
        `${header.offsetHeight}px`
      );
    };

    syncHeaderHeight();
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(header);
    window.addEventListener('resize', syncHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
    };
  }, [menuOpen, isScrolled]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  // Блокировка скролла при открытой модалке
  useEffect(() => {
    document.body.style.overflow = modalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen]);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`header ${isScrolled ? 'header--scrolled' : ''}`}
      >
        <div className="container header__container">
          <Link to="/" className="header__logo">
            <img src="/images/logo.png" alt={t('brand.name')} className="header__logo-img" />
            <div className="header__logo-text">
              <span className="header__logo-name">{t('brand.name')}</span>
              <span className="header__logo-desc">{t('brand.desc')}</span>
            </div>
          </Link>

          <nav className="header__nav">
            <button onClick={() => scrollToSection('services')} className="header__link">
              {t('nav.services')}
            </button>
            <button onClick={() => scrollToSection('prices')} className="header__link">
              {t('nav.prices')}
            </button>
            <button onClick={() => scrollToSection('about')} className="header__link">
              {t('nav.about')}
            </button>
            <button onClick={() => scrollToSection('contacts')} className="header__link">
              {t('nav.contacts')}
            </button>
          </nav>

          <div className="header__actions">
            <LanguageSwitcher />

            <a href={`tel:${phone.replace(/\s/g, '')}`} className="header__phone">
              <span className="header__phone-icon">📞</span>
              <div className="header__phone-info">
                <span className="header__phone-number">{phone}</span>
                <span className="header__phone-hours">{t('nav.workHours')}</span>
              </div>
            </a>

            <button
              className="btn btn-primary header__cta"
              onClick={() => setModalOpen(true)}
            >
              {t('nav.contactUs')}
            </button>
          </div>

          <button
            className={`header__burger ${menuOpen ? 'header__burger--open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {menuOpen && (
          <div className="header__mobile-menu">
            <LanguageSwitcher mobile />
            <button onClick={() => scrollToSection('services')}>{t('nav.services')}</button>
            <button onClick={() => scrollToSection('prices')}>{t('nav.prices')}</button>
            <button onClick={() => scrollToSection('about')}>{t('nav.about')}</button>
            <button onClick={() => scrollToSection('contacts')}>{t('nav.contacts')}</button>
            <a href={`tel:${phone.replace(/\s/g, '')}`} className="header__mobile-phone">
              📞 {phone}
            </a>

            <button
              className="btn btn-primary"
              onClick={() => { setMenuOpen(false); setModalOpen(true); }}
              style={{ width: '100%' }}
            >
              {t('nav.contactUs')}
            </button>
          </div>
        )}
      </header>

      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default Header;
